import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MIPS_BLOCK_REASONS } from '@constants/mips-block-reasons';
import { MipsSubmissionBlockActions } from '@store/actions/mips-submission-block/mips-submission-block.actions';
import { selectMipsSubmissionBlockSaving } from '@store/selectors/mips-submission-block/mips-submission-block.selectors';

export interface MipsSubmissionBlockModalData {
  organizationId: number;
  organizationName: string;
  selectedReasonCodes: string[];
}

@Component({
  selector: 'app-mips-submission-block-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatCheckboxModule],
  templateUrl: './mips-submission-block-modal.component.html',
  styleUrl: './mips-submission-block-modal.component.css',
})
export class MipsSubmissionBlockModalComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialogRef = inject(MatDialogRef<MipsSubmissionBlockModalComponent>);
  readonly data = inject<MipsSubmissionBlockModalData>(MAT_DIALOG_DATA);
  private readonly destroy$ = new Subject<void>();

  readonly reasons = MIPS_BLOCK_REASONS;
  selectedReasonCodes: string[] = [...this.data.selectedReasonCodes];
  saving$ = this.store.select(selectMipsSubmissionBlockSaving);

  get isEditMode(): boolean {
    return this.data.selectedReasonCodes.length > 0;
  }

  ngOnInit(): void {
    this.actions$
      .pipe(
        ofType(MipsSubmissionBlockActions.blockOrganizationSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.dialogRef.close());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isSelected(code: string): boolean {
    return this.selectedReasonCodes.includes(code);
  }

  toggleReason(code: string, isChecked: boolean): void {
    this.selectedReasonCodes = isChecked
      ? [...this.selectedReasonCodes, code]
      : this.selectedReasonCodes.filter((existing) => existing !== code);
  }

  onSave(): void {
    if (this.selectedReasonCodes.length === 0) return;
    this.store.dispatch(
      MipsSubmissionBlockActions.blockOrganization({
        request: {
          organizationId: this.data.organizationId,
          reasonCodes: this.selectedReasonCodes,
        },
      })
    );
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
